/* eslint-disable no-param-reassign */
import { Buffer as AutoBuffer, API } from '@skeepers/i18n';

import { routerStrategy, queryStrategy } from '../data/strategies';
import { parseJsonOrNull } from '../utils';

export function makeSolution({ api }) {
    const configAPI = new API({ url: api.url, template: null });
    const buffer = new AutoBuffer({ onFlush: configAPI.reportUsage.bind(configAPI) });

    return {
        namespaced: true,
        state: {
            id: null,
            type: null,
            config: {},
            company: { name: '' },
            language: null,
        },
        getters: {
            hasCasting(state) {
                return !!state.config.CASTING;
            },
            isCasting(state) {
                return state.type === 'CASTING';
            },
            showUploadInformation({ config }) {
                return parseJsonOrNull(config.INFORMATION_ASK) || false;
            },
            showUserVideos(state, { isCasting }) {
                return !isCasting;
            },
            getCompanyName(state) {
                return state.company.name;
            },
            /**
             * If platform does not have a previous casting, show home steps
             */
            showHomeSteps(state, { hasCasting }) {
                return !hasCasting;
            },
            homeSteps({ config }) {
                return parseJsonOrNull(config.HOME_STEPS);
            },
            briefSteps({ config, smartUploader }) {
                const smartUploaderBrief = smartUploader && Array.isArray(smartUploader.sequences) && smartUploader.sequences.length
                    ? smartUploader.sequences
                    : null;
                return smartUploaderBrief || parseJsonOrNull(config.BRIEF_STEPS);
            },
            briefRules({ config }) {
                return [
                    {
                        name: config.BRIEF_RULES_GENERAL_TITLE,
                        do: parseJsonOrNull(config.BRIEF_RULES_GENERAL_DO) || [],
                        dont: parseJsonOrNull(config.BRIEF_RULES_GENERAL_DONT) || [],
                    }, {
                        name: config.BRIEF_RULES_IMAGE_TITLE,
                        do: parseJsonOrNull(config.BRIEF_RULES_IMAGE_DO) || [],
                        dont: parseJsonOrNull(config.BRIEF_RULES_IMAGE_DONT) || [],
                    }, {
                        name: config.BRIEF_RULES_SOUND_TITLE,
                        do: parseJsonOrNull(config.BRIEF_RULES_SOUND_DO) || [],
                        dont: parseJsonOrNull(config.BRIEF_RULES_SOUND_DONT) || [],
                    }, {
                        name: config.BRIEF_RULES_OTHER_TITLE,
                        do: parseJsonOrNull(config.BRIEF_RULES_OTHER_DO) || [],
                        dont: parseJsonOrNull(config.BRIEF_RULES_OTHER_DONT) || [],
                    },
                ];
            },
            briefVideoUrl({ config, smartUploader }) {
                // on both production and staging DBs this part of the url is used for default video brief.
                const isDefaultVideo = config.BRIEF_VIDEO_URL.includes('upncast.mp4');
                // if there is a smart uploader linked but there is no custom video brief uploaded
                // we need to hide a default video because it's incorrect - for horizontal videos only
                return smartUploader && isDefaultVideo ? null : config.BRIEF_VIDEO_URL;
            },
        },
        mutations: {
            SET_ID(state, id) {
                state.id = id;
            },
            SET_COMPANY_NAME(state, companyName) {
                state.company.name = companyName
            },
            SET_SMART_UPLOADER(state, linkedSmartUploader) {
                state.smartUploader = linkedSmartUploader;
            },
            SET_TYPE(state, type) {
                state.type = type;
                configAPI.template = type;
            },
            SET_CONFIG(state, config) {
                state.config = new Proxy(config, {
                    /**
                     * Intercept the translated value
                     */
                    get(target, key) {
                        // Only applies to own properties
                        if (target.hasOwnProperty(key)) {
                            buffer.add(key);
                        }
                        return target[key];
                    },
                });
            },
            SET_LANGUAGE(state, language) {
                state.language = language;
            },
        },
        actions: {
            async updateSolution({ commit }) {
                // Get solution data by trying to short-circuit using router-injected config
                let solution = routerStrategy('solution');

                // Fallback to hash parameter
                if (!solution) {
                    const id = queryStrategy('sid');

                    if (!id) {
                        throw new Error('Solution ID could not be found');
                    } else {
                        solution = await api.getSolution(id);
                    }
                }

                const rootStyles = document.querySelector(':root').style;
                rootStyles.setProperty('--COLOR_PRIMARY', solution.configuration.COLOR_PRIMARY);

                commit('SET_ID', solution.id);
                commit('SET_SMART_UPLOADER', solution.linkedSmartUploader);
                commit('SET_TYPE', solution.type);
                commit('SET_CONFIG', solution.configuration);
                commit('SET_LANGUAGE', solution.language);
                commit('SET_COMPANY_NAME', solution.company.name);
            },
        },
    };
}
